import {
  ConversationContext,
  ConversationInlineData,
  ConversationMessage,
  ConversationMessageStatus,
  MessagePartData,
  ConversationParticipant,
  ConversationStatus,
  StartConversationParams,
} from '@stripe-internal/conversations-client';
import {
  ConversationsApiDataConversation,
  ConversationsApiDataMessage,
  ConversationsApiDataMessagePart,
  ConversationsApiDataMessagePartContentSelectedOptionPartContent,
  ConversationsApiDataParticipant,
} from '../generated/types';
import {ErrorResponse, UNAUTHORIZED_ERROR} from './ApiMethods';

type FrameworkConversationData = {
  id: string;
  status: ConversationStatus;
  messages: Record<string, ConversationMessage>;
  participants: Record<string, ConversationParticipant>;
  context: ConversationContext;
};

type OptionsPartMetadata = {
  responseId?: string;
  feedback?: ConversationMessage['feedback'];
  selectedOptionId?: string;
  recommendedOptionId?: string;
  purpose?: string;
};

type ConversationDiff = {
  conversation: FrameworkConversationData;
  newMessages: ConversationMessage[];
};

const ENABLED_INPUT_MODES = ['text', 'prompt-option'];

export class FrameworkAdapter {
  static toClientErrorResponse(response: ErrorResponse): {
    code: number;
    error: string;
  } {
    if (response.error === UNAUTHORIZED_ERROR) {
      return {code: 401, error: UNAUTHORIZED_ERROR};
    }
    return {code: response.code, error: response.error};
  }

  private static roleFromType(type?: string): ConversationParticipant['role'] {
    const normalizedType = type?.toLowerCase();

    switch (normalizedType) {
      case 'human_agent':
        return 'agent';
      case 'bot':
      case 'assistant':
      case 'system':
        return 'bot';
      case 'user':
      default:
        return 'user';
    }
  }

  static participantTypeFromRole(
    role: ConversationParticipant['role'],
  ): string {
    if (role === 'agent') {
      return 'human_agent';
    }
    if (role === 'bot') {
      return 'assistant';
    }
    return 'user';
  }

  private static displayNameForRole(
    role: ConversationParticipant['role'],
  ): string {
    if (role === 'agent') {
      return 'Agent';
    }
    if (role === 'bot') {
      return 'Assistant';
    }
    return 'User';
  }

  private static getOrBuildParticipant(
    participantsById: Record<string, ConversationParticipant>,
    id: string,
    role: ConversationParticipant['role'],
    displayName?: string,
  ): ConversationParticipant {
    if (!participantsById[id]) {
      participantsById[id] = {
        id,
        role,
        displayName: displayName || this.displayNameForRole(role),
      };
    }

    return participantsById[id];
  }

  private static apiParticipantToFrameworkParticipant(
    participant: ConversationsApiDataParticipant,
    participantsById: Record<string, ConversationParticipant>,
  ): ConversationParticipant {
    const role = this.roleFromType(participant.type);
    return this.getOrBuildParticipant(
      participantsById,
      participant.id,
      role,
      participant.display_name,
    );
  }

  private static buildParticipantId(
    message: ConversationsApiDataMessage,
  ): string {
    if (message.participant_id) {
      return message.participant_id;
    }

    const role = this.roleFromType(message.participant_type);
    return `synthetic-${role}-participant`;
  }

  private static resolveParticipantForMessage(
    message: ConversationsApiDataMessage,
    participantsById: Record<string, ConversationParticipant>,
    user?: NonNullable<StartConversationParams['participant']>,
  ): ConversationParticipant {
    if (message.participant_id && participantsById[message.participant_id]) {
      return participantsById[message.participant_id];
    }

    const role = this.roleFromType(message.participant_type);
    const participantId = this.buildParticipantId(message);
    const displayName = role === 'user' ? user?.displayName : undefined;

    return this.getOrBuildParticipant(
      participantsById,
      participantId,
      role,
      displayName,
    );
  }

  private static buildMessagePart(
    part: ConversationsApiDataMessagePart,
  ): MessagePartData {
    return {
      id: String(part.id),
      type: part.type,
      content: part.content,
      ...(part.dynamic ? {dynamic: part.dynamic} : {}),
    };
  }

  /**
   * Builds the optional metadata bag carried on BaseConversationMessage.data.
   * Forwards page_context and part-level dynamic for rendering.
   * Message-level dynamic drives polling (via shouldPoll in the handler),
   * not rendering.
   */
  private static buildBaseMessageMetadata(
    message: ConversationsApiDataMessage,
    part?: ConversationsApiDataMessagePart,
  ) {
    const metadata: Record<string, unknown> = {};
    if (message.page_context) {
      metadata.pageContext = message.page_context;
    }
    if (part?.dynamic) {
      metadata.dynamic = part.dynamic;
    }
    if (Object.keys(metadata).length === 0) {
      return undefined;
    }
    return metadata;
  }

  private static enabledInputModes(
    apiConversation: ConversationsApiDataConversation,
  ): string[] {
    const assistantMessage = [...apiConversation.messages]
      .reverse()
      .find((message) => this.roleFromType(message.participant_type) === 'bot');
    if (assistantMessage) {
      if (assistantMessage.disable_input === true) {
        return [];
      }

      return [...ENABLED_INPUT_MODES];
    }

    return [...ENABLED_INPUT_MODES];
  }

  private static messagePartId(
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
  ): string {
    return `${message.id}.${part.id}`;
  }

  private static parseInlineDataRendering(
    rendering: string,
  ): ConversationInlineData['data'] {
    try {
      return JSON.parse(rendering) as ConversationInlineData['data'];
    } catch {
      // FIXME(framework-adapter): report parse failures once this static adapter has observability plumbing.
      return [];
    }
  }

  private static stringValue(
    record: Record<string, unknown>,
    ...keys: string[]
  ): string | undefined {
    for (const key of keys) {
      const value = record[key];
      if (typeof value === 'string' && value.length > 0) {
        return value;
      }
    }
    return undefined;
  }

  // FIXME(framework-adapter): The API does not yet plumb these fields explicitly
  // on options_part_content; we parse them optimistically out of part.dynamic
  // when present so live and rehydrated messages carry the same shape.
  private static optionsMetadataFromDynamic(
    dynamic?: string,
  ): OptionsPartMetadata {
    if (!dynamic) {
      return {};
    }

    try {
      const parsed = JSON.parse(dynamic) as unknown;
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        return {};
      }

      const record = parsed as Record<string, unknown>;
      const {feedback} = record;
      return {
        responseId: this.stringValue(record, 'responseId', 'response_id'),
        feedback:
          feedback && typeof feedback === 'object'
            ? (feedback as ConversationMessage['feedback'])
            : undefined,
        selectedOptionId: this.stringValue(
          record,
          'selectedOptionId',
          'selected_option_id',
        ),
        recommendedOptionId: this.stringValue(
          record,
          'recommendedOptionId',
          'recommended_option_id',
        ),
        purpose: this.stringValue(record, 'purpose'),
      };
    } catch {
      return {};
    }
  }

  private static normalizeThumbsFeedback(
    thumbsFeedback?: string,
  ): NonNullable<ConversationMessage['feedback']>['value'] | undefined {
    if (
      thumbsFeedback === 'none' ||
      thumbsFeedback === 'thumbs_up' ||
      thumbsFeedback === 'thumbs_down'
    ) {
      return thumbsFeedback;
    }
    return undefined;
  }

  private static fallbackResponseMetadata(
    message: ConversationsApiDataMessage,
    participant: ConversationParticipant,
  ): Pick<ConversationMessage, 'feedback' | 'responseId'> {
    if (participant.role !== 'bot') {
      return {};
    }

    const feedbackValue = this.normalizeThumbsFeedback(message.thumbs_feedback);
    if (!feedbackValue) {
      return {};
    }

    return {
      responseId: message.id,
      feedback: {
        action: 'log_only',
        value: feedbackValue,
      },
    };
  }

  private static fromApiPart(
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
    participantsById: Record<string, ConversationParticipant>,
    user?: NonNullable<StartConversationParams['participant']>,
  ): ConversationMessage | null {
    const participant = this.resolveParticipantForMessage(
      message,
      participantsById,
      user,
    );
    const timestamp = this.messageTimestamp(message.created);
    const id = this.messagePartId(message, part);
    const metadata = this.buildBaseMessageMetadata(message, part);

    if (part.type === 'text') {
      return {
        id,
        participant,
        timestamp,
        data: metadata,
        ...this.fallbackResponseMetadata(message, participant),
        type: 'text-conversation-message',
        text: part.content.text,
      };
    }

    if (part.type === 'options') {
      const optionsMetadata = this.optionsMetadataFromDynamic(part.dynamic);
      const fallbackResponseMetadata = this.fallbackResponseMetadata(
        message,
        participant,
      );
      const responseId =
        optionsMetadata.responseId ?? fallbackResponseMetadata.responseId;
      const feedback =
        optionsMetadata.feedback ?? fallbackResponseMetadata.feedback;
      const options = part.content.options.map((option) => {
        return {
          id: option.id,
          label: option.text,
        };
      });
      const optionsBase = {
        id,
        participant,
        timestamp,
        data: metadata,
        label: part.content.text,
        options,
        ...(responseId === undefined ? {} : {responseId}),
        ...(feedback === undefined ? {} : {feedback}),
        selectedOptionId: optionsMetadata.selectedOptionId,
        recommendedOptionId: optionsMetadata.recommendedOptionId,
        purpose: optionsMetadata.purpose,
      };
      if (part.content.style === 'dropdown') {
        return {
          ...optionsBase,
          type: 'select-options-conversation-message',
        };
      }
      return {
        ...optionsBase,
        type: 'button-options-conversation-message',
      };
    }

    if (part.type === 'selected_option') {
      return null;
    }

    if (part.type === 'show_inline_data') {
      return {
        id,
        participant,
        timestamp,
        data: {
          ...metadata,
          conversationsApi: {messageId: message.id, partId: part.id},
        },
        ...this.fallbackResponseMetadata(message, participant),
        type: 'inline-data-conversation-message',
        effectId: id,
        inlineData: {
          name: part.content.inline_data_name,
          style: part.content
            .inline_data_style as ConversationInlineData['style'],
          isDone: true,
          data: this.parseInlineDataRendering(part.content.rendering),
        },
      };
    }

    if (part.type === 'buttons') {
      return {
        id,
        participant,
        timestamp,
        type: 'rich-text-conversation-message',
        text: part.content.text,
        data: {
          type: 'buttons-conversation-message',
          buttons: part.content.buttons.map((button) => {
            return {
              title: button.title,
              url: button.url,
            };
          }),
        },
      };
    }

    const customPart = this.buildMessagePart(part);
    return {
      id,
      participant,
      timestamp,
      data: metadata,
      type: 'custom-part-conversation-message',
      part: customPart,
    };
  }

  private static fallbackSelectedOptionMessage(
    message: ConversationsApiDataMessage,
    part: ConversationsApiDataMessagePart,
    selectedOption: ConversationsApiDataMessagePartContentSelectedOptionPartContent,
    participantsById: Record<string, ConversationParticipant>,
    user?: NonNullable<StartConversationParams['participant']>,
  ): ConversationMessage {
    const participant = this.resolveParticipantForMessage(
      message,
      participantsById,
      user,
    );
    return {
      id: this.messagePartId(message, part),
      participant,
      timestamp: this.messageTimestamp(message.created),
      data: this.buildBaseMessageMetadata(message),
      type: 'text-conversation-message',
      text: selectedOption.text,
    };
  }

  private static messageTimestamp(created: number): number {
    return created * 1000;
  }

  private static findMostRecentOptionsMessage(
    message: ConversationsApiDataMessage,
    apiConversation: ConversationsApiDataConversation,
    messagesById: Record<string, ConversationMessage>,
  ): ConversationMessage | undefined {
    const messageIndex = apiConversation.messages.indexOf(message);
    for (let i = messageIndex - 1; i >= 0; i -= 1) {
      const candidate = apiConversation.messages[i];
      for (const part of candidate.parts) {
        const id = this.messagePartId(candidate, part);
        const mapped = messagesById[id];
        if (
          mapped?.type === 'button-options-conversation-message' ||
          mapped?.type === 'select-options-conversation-message'
        ) {
          return mapped;
        }
      }
    }
    return undefined;
  }

  private static applySelectedOptionParts({
    apiConversation,
    messagesById,
    participantsById,
    user,
  }: {
    apiConversation: ConversationsApiDataConversation;
    messagesById: Record<string, ConversationMessage>;
    participantsById: Record<string, ConversationParticipant>;
    user?: NonNullable<StartConversationParams['participant']>;
  }): void {
    apiConversation.messages.forEach((message) => {
      message.parts.forEach((part) => {
        if (part.type !== 'selected_option') {
          return;
        }

        const optionsMessage =
          (message.creator_part_id
            ? messagesById[message.creator_part_id]
            : undefined) ??
          this.findMostRecentOptionsMessage(
            message,
            apiConversation,
            messagesById,
          );
        if (
          optionsMessage?.type === 'button-options-conversation-message' ||
          optionsMessage?.type === 'select-options-conversation-message'
        ) {
          const participant =
            user ||
            this.resolveParticipantForMessage(message, participantsById, user);
          messagesById[optionsMessage.id] = {
            ...optionsMessage,
            participant,
            selectedOptionId: part.content.id,
            status: ConversationMessageStatus.Sent,
          };
          return;
        }

        // FIXME(framework-adapter): report orphan selections once this static adapter has observability plumbing.
        const fallbackMessage = this.fallbackSelectedOptionMessage(
          message,
          part,
          part.content,
          participantsById,
          user,
        );
        messagesById[fallbackMessage.id] = fallbackMessage;
      });
    });
  }

  private static statusFromApi(
    _apiConversation: ConversationsApiDataConversation,
  ): ConversationStatus {
    // FIXME(framework-adapter): The API does not yet expose conversation status.
    return ConversationStatus.Active;
  }

  static fromApiConversation({
    apiConversation,
    requestContext,
    user,
  }: {
    apiConversation: ConversationsApiDataConversation;
    requestContext: NonNullable<StartConversationParams['context']>;
    user?: NonNullable<StartConversationParams['participant']>;
  }): FrameworkConversationData {
    const participantsById: Record<string, ConversationParticipant> = {};
    if (user) {
      participantsById[user.id] = user;
    }
    apiConversation.participants.forEach((participant) => {
      this.apiParticipantToFrameworkParticipant(participant, participantsById);
    });

    const messagesById: Record<string, ConversationMessage> = {};
    apiConversation.messages.forEach((message) => {
      message.parts.forEach((part) => {
        const mapped = this.fromApiPart(message, part, participantsById, user);
        if (mapped) {
          messagesById[mapped.id] = mapped;
        }
      });
    });
    this.applySelectedOptionParts({
      apiConversation,
      messagesById,
      participantsById,
      user,
    });

    const triggerPoint =
      apiConversation.trigger_point ||
      requestContext.triggerParams?.triggerPoint;
    const context: ConversationContext = {
      ...requestContext,
      audience: apiConversation.audience || requestContext.audience,
      entryPoint: apiConversation.entry_point || requestContext.entryPoint,
      channel: 'bot',
      enabledInputModes: this.enabledInputModes(apiConversation),
    };
    if (triggerPoint) {
      context.triggerParams = {
        ...(requestContext.triggerParams ?? {}),
        triggerPoint,
      };
    }

    return {
      id: apiConversation.id,
      status: this.statusFromApi(apiConversation),
      messages: messagesById,
      participants: participantsById,
      context,
    };
  }

  static fromApiMessages(
    apiMessages: ConversationsApiDataMessage[],
    participantsById: Record<string, ConversationParticipant>,
    user?: NonNullable<StartConversationParams['participant']>,
  ): Record<string, ConversationMessage> {
    const messagesById: Record<string, ConversationMessage> = {};
    apiMessages.forEach((message) => {
      message.parts.forEach((part) => {
        const mapped = this.fromApiPart(message, part, participantsById, user);
        if (mapped) {
          messagesById[mapped.id] = mapped;
        }
      });
    });
    return messagesById;
  }

  static diffConversation({
    previousMessageIds,
    apiConversation,
    requestContext,
    user,
    minimumTimestamp,
  }: {
    previousMessageIds: Iterable<string>;
    apiConversation: ConversationsApiDataConversation;
    requestContext: NonNullable<StartConversationParams['context']>;
    user: NonNullable<StartConversationParams['participant']>;
    minimumTimestamp?: number;
  }): ConversationDiff {
    const existingIds = new Set(previousMessageIds);
    const conversation = this.fromApiConversation({
      apiConversation,
      requestContext,
      user,
    });
    const newMessages = Object.values(conversation.messages).filter(
      (message) => {
        if (existingIds.has(message.id)) {
          return false;
        }
        if (message.participant.id === user.id) {
          return false;
        }
        if (
          typeof minimumTimestamp === 'number' &&
          message.timestamp < minimumTimestamp
        ) {
          return false;
        }
        return true;
      },
    );

    return {conversation, newMessages};
  }
}
